import { useEffect, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { LocationSelect } from "@/components/LocationSelect";
import { ControlDetail } from "@/components/ControlDetail";
import { HistoryView } from "@/components/HistoryView";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtTemp } from "@/lib/format";

export default function AdminControles() {
  const { activeLocation } = useAuth();
  const [controls, setControls] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (!activeLocation) { setControls([]); return; }
    api.get(`/controls/today?location_id=${activeLocation}`).then(({ data }) => setControls(data)).catch(() => setControls([]));
  }, [activeLocation]);

  const filters = [
    ["ALL", "Todos"], ["PENDING", "Pendientes"], ["LATE", "Atrasados"],
    ["MISSED", "Omitidos"], ["COMPLETED", "Realizados"], ["INCIDENT", "Fuera de rango"],
  ];
  const shown = controls.filter((c) => filter === "ALL" || c.status === filter);

  return (
    <div>
      <PageHeader title="Controles" subtitle="Controles de hoy e historial" right={<LocationSelect />} />
      <Tabs defaultValue="hoy">
        <TabsList className="mb-4">
          <TabsTrigger value="hoy" data-testid="tab-controles-hoy">Hoy</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-controles-historial">Historial</TabsTrigger>
        </TabsList>
        <TabsContent value="hoy">
          <div className="mb-4 flex flex-wrap gap-2">
            {filters.map(([v, l]) => (
              <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${filter === v ? "bg-primary text-primary-foreground" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
            ))}
          </div>
          {!activeLocation ? (
            <EmptyState icon={ClipboardCheck} title="Selecciona un local" />
          ) : shown.length === 0 ? (
            <EmptyState icon={ClipboardCheck} title="Sin controles" subtitle="No hay controles en esta vista." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {shown.map((c) => {
                const done = c.status === "COMPLETED" || c.status === "INCIDENT";
                return (
                  <button key={c.id} data-testid={`admin-control-${c.equipment_id}`} disabled={!done} onClick={() => done && c.temperature_check_id && setDetailId(c.temperature_check_id)}
                    className="flex w-full items-center gap-3 border-b border-slate-100 p-4 text-left last:border-0 disabled:cursor-default hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{c.equipment_name}</p>
                      <p className="text-xs text-slate-500">{c.area_name} · {c.shift_name}</p>
                    </div>
                    {c.temperature != null && <span className="tnum text-sm font-medium">{fmtTemp(c.temperature, c.unit)}</span>}
                    <StatusPill status={c.status} />
                  </button>
                );
              })}
            </div>
          )}
          <ControlDetail checkId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
        </TabsContent>
        <TabsContent value="historial">
          <HistoryView isAdmin />
        </TabsContent>
      </Tabs>
    </div>
  );
}
